import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Info,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INCOME_CATEGORIES = [
  { value: 'rent_income', label: 'Rental Income' },
  { value: 'other_income', label: 'Other Income' },
];

const EXPENSE_CATEGORIES = [
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'management_fees', label: 'Management Fees' },
  { value: 'mortgage_interest', label: 'Mortgage Interest' },
  { value: 'ground_rent_service_charge', label: 'Ground Rent / Service Charge' },
  { value: 'legal_professional', label: 'Legal & Professional' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'travel', label: 'Travel' },
  { value: 'other_expenses', label: 'Other Expenses' },
];

// CT600 (Corporation Tax) category lists — used when ownership_type === 'company'
const CT600_INCOME_CATEGORIES = [
  { value: 'rent_income', label: 'Trading Income' },
  { value: 'other_income', label: 'Other Income' },
];

const CT600_EXPENSE_CATEGORIES = [
  { value: 'mortgage_interest', label: 'Loan Interest' },
  { value: 'management_fees', label: 'Management Fees' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'ground_rent_service_charge', label: 'Ground Rent / Service Charge' },
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance' },
  { value: 'legal_professional', label: 'Legal & Professional' },
  { value: 'accountancy_fees', label: 'Accountancy Fees' },
  { value: 'directors_remuneration', label: "Directors' Remuneration" },
  { value: 'company_pension', label: 'Company Pension Contributions' },
  { value: 'capital_allowances', label: 'Capital Allowances — AIA' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'travel', label: 'Travel' },
  { value: 'other_expenses', label: 'Other Expenses' },
];

const SA105_LABEL_BY_CATEGORY = {
  repairs_maintenance: 'Repairs & Maintenance (Box 29)',
  insurance: 'Insurance (Box 28)',
  management_fees: 'Management Fees (Box 27)',
  mortgage_interest: 'Mortgage Interest (Box 26)',
  ground_rent_service_charge: 'Ground Rent / Service Charge (Box 28)',
  legal_professional: 'Legal & Professional (Box 30)',
  utilities: 'Utilities (Box 30)',
  travel: 'Travel (Box 30)',
  other_expenses: 'Other Expenses (Box 30)',
};

const DESCRIPTION_HELPERS = {
  rent_income: 'Include tenant name and rent period (for example: Flat 2A rent for March 2026).',
  other_income: 'Include the income source and period (for example: Insurance refund - Q1 2026).',
  repairs_maintenance: 'Include contractor/trade and work done (for example: ABC Plumbing - boiler repair).',
  insurance: 'Include insurer and policy reference or period (for example: Aviva premium - policy 12345).',
  management_fees: 'Include agent/manager and period (for example: Letting fee - March 2026).',
  mortgage_interest: 'Include your lender and statement period (for example: Barclays mortgage interest - March 2026).',
  ground_rent_service_charge: 'Include freeholder/management company and period covered.',
  legal_professional: 'Include firm/adviser and purpose (for example: conveyancing review).',
  utilities: 'Include provider, meter/property and billing period.',
  travel: 'Include journey purpose and route/date (for example: Property inspection - Leeds return).',
  other_expenses: 'Include payee and reason so this can be audited later.',
};

const getDescriptionHelperText = (type, category) => {
  if (category && DESCRIPTION_HELPERS[category]) return DESCRIPTION_HELPERS[category];
  if (type === 'income') return 'Include who paid you and what period it covers.';
  return 'Include who was paid and what the expense was for.';
};

const getExpenseCategoryColor = (category, categories = EXPENSE_CATEGORIES) => {
  const order = categories.map((item) => item.value);
  const explicitIndex = order.indexOf(category);
  if (explicitIndex >= 0) return PIE_COLORS[explicitIndex % PIE_COLORS.length];
  const key = String(category || 'unknown');
  const hash = key.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return PIE_COLORS[hash % PIE_COLORS.length];
};

const ACCOUNT_OPTIONS = [
  { value: 'cash', label: 'Cash', type: 'asset' },
  { value: 'property_asset', label: 'Property Asset', type: 'asset' },
  { value: 'mortgage', label: 'Mortgage', type: 'liability' },
  { value: 'deposit_liability', label: 'Deposit Liability', type: 'liability' },
  { value: 'owner_equity', label: 'Owner Equity', type: 'equity' },
  { value: 'rental_income', label: 'Rental Income', type: 'income' },
  { value: 'repairs_expense', label: 'Repairs Expense', type: 'expense' },
  { value: 'insurance_expense', label: 'Insurance Expense', type: 'expense' },
  { value: 'management_expense', label: 'Management Expense', type: 'expense' },
  { value: 'mortgage_interest_expense', label: 'Mortgage Interest Expense', type: 'expense' },
  { value: 'ground_rent_service_charge_expense', label: 'Ground Rent / Service Charge Expense', type: 'expense' },
  { value: 'legal_professional_expense', label: 'Legal & Professional Expense', type: 'expense' },
  { value: 'utilities_expense', label: 'Utilities Expense', type: 'expense' },
  { value: 'travel_expense', label: 'Travel Expense', type: 'expense' },
  { value: 'accountancy_fees_expense', label: 'Accountancy Fees Expense', type: 'expense' },
  { value: 'directors_remuneration_expense', label: "Directors' Remuneration Expense", type: 'expense' },
  { value: 'company_pension_expense', label: 'Company Pension Contributions Expense', type: 'expense' },
  { value: 'capital_allowances_expense', label: 'Capital Allowances Expense', type: 'expense' },
  { value: 'other_expense', label: 'Other Expense', type: 'expense' },
];

const COMPANY_ONLY_ACCOUNT_VALUES = new Set([
  'accountancy_fees_expense',
  'directors_remuneration_expense',
  'company_pension_expense',
  'capital_allowances_expense',
]);

const MAPPING_FIELDS = [
  { key: 'date', label: 'Date', required: true },
  { key: 'amount', label: 'Amount', required: false },
  { key: 'paid_in', label: 'Paid in', required: false },
  { key: 'paid_out', label: 'Paid out', required: false },
  { key: 'description', label: 'Description', required: true },
  { key: 'type', label: 'Type', required: false },
];

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899', '#06b6d4'];
const FINANCE_CACHE_VERSION = 'v2';
const FINANCE_REQUEST_TIMEOUT_MS = 12000;

const formatCurrency = (value) => `£${Number(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cleanExpenseDisplayLabel = (label, accountType) => (accountType === 'expense' && typeof label === 'string' && label.endsWith(' Expense')
  ? label.slice(0, -8)
  : label);

const getGroupingLabel = (grouping) => {
  if (grouping === 'monthly') return 'Monthly';
  if (grouping === 'quarterly') return 'Quarterly';
  return 'Yearly';
};

const getSa105TooltipText = (sa105Label) => {
  if (!sa105Label) return '';
  const parts = String(sa105Label).split('|').map((part) => part.trim()).filter(Boolean);
  const boxes = parts.map((part) => {
    const match = part.match(/\(Box\s*([^\)]+)\)/i);
    return match ? `Box ${match[1].trim()}` : part;
  });
  return `SA105: ${boxes.join(' • ')}`;
};

function Sa105Hint({ sa105Label }) {
  const [isOpen, setIsOpen] = useState(false);
  if (!sa105Label) return null;
  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-help items-center text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded p-0.5"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Show SA105 information"
        >
          <Info className="h-3.5 w-3.5" />
          <span className="sr-only">{getSa105TooltipText(sa105Label)}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <span>{getSa105TooltipText(sa105Label)}</span>
      </TooltipContent>
    </Tooltip>
  );
}

const getCurrentTaxYear = () => {
  const now = new Date();
  return now.getMonth() + 1 > 4 || (now.getMonth() + 1 === 4 && now.getDate() >= 6)
    ? now.getFullYear()
    : now.getFullYear() - 1;
};

const getCurrentCompanyAccountingYear = (yearEnd = '03-31') => {
  const now = new Date();
  const [monthRaw, dayRaw] = String(yearEnd || '03-31').split('-');
  const endMonth = Math.max(1, Math.min(12, parseInt(monthRaw, 10) || 3));
  const endDay = Math.max(1, Math.min(31, parseInt(dayRaw, 10) || 31));
  const yearEndThisYear = new Date(now.getFullYear(), endMonth - 1, endDay, 23, 59, 59, 999);
  return now > yearEndThisYear ? now.getFullYear() : now.getFullYear() - 1;
};

const getCurrentFinanceYear = (user) => (
  user?.ownership_type === 'company'
    ? getCurrentCompanyAccountingYear(user?.company_year_end)
    : getCurrentTaxYear()
);

const getFinanceYearForDate = (user, value) => {
  const parsed = value ? new Date(`${value}T00:00:00`) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  if (user?.ownership_type !== 'company') {
    return date.getMonth() + 1 > 4 || (date.getMonth() + 1 === 4 && date.getDate() >= 6)
      ? date.getFullYear()
      : date.getFullYear() - 1;
  }

  const [monthRaw, dayRaw] = String(user?.company_year_end || '03-31').split('-');
  const endMonth = Math.max(1, Math.min(12, parseInt(monthRaw, 10) || 3));
  const endDay = Math.max(1, Math.min(31, parseInt(dayRaw, 10) || 31));
  const yearEndThisYear = new Date(date.getFullYear(), endMonth - 1, endDay, 23, 59, 59, 999);
  return date > yearEndThisYear ? date.getFullYear() : date.getFullYear() - 1;
};

const buildRecurringDates = (startDate, freq, count) => {
  const dates = [];
  const current = new Date(startDate);
  for (let i = 0; i < count; i += 1) {
    dates.push(current.toISOString().slice(0, 10));
    if (freq === 'weekly') current.setDate(current.getDate() + 7);
    if (freq === 'monthly') current.setMonth(current.getMonth() + 1);
    if (freq === 'quarterly') current.setMonth(current.getMonth() + 3);
    if (freq === 'yearly') current.setFullYear(current.getFullYear() + 1);
  }
  return dates;
};

const createEmptyJournalLine = () => ({
  id: crypto.randomUUID(),
  account: '',
  debit: '',
  credit: '',
  description: '',
});

const subTabs = [
  ['transactions', 'Transactions'],
  ['import', 'Import/Export CSV'],
  ['analytics', 'Tax and Analytics'],
  ['reports', 'Reports'],
];

export function FinancePanel({ properties = [], isActive = false }) {
  const { user } = useAuth();
  const isCompany = user?.ownership_type === 'company';
  const taxSummaryLabel = isCompany ? 'CT600 Tax Summary' : 'SA105 Tax Summary';
  const taxCategoryLabel = isCompany ? 'CT600 Categories' : 'SA105 Categories';
  const taxExportFormatLabel = isCompany ? 'CT600' : 'SA105';
  const taxIncomeLabel = isCompany ? 'Trading Income' : 'Rental Income (Box 20)';

  const [isOnline, setIsOnline] = useState(() => typeof navigator === 'undefined' ? true : navigator.onLine);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 640px)').matches;
  });
  const [usingCachedSummary, setUsingCachedSummary] = useState(false);
  const [usingCachedAnalytics, setUsingCachedAnalytics] = useState(false);
  const [usingCachedReports, setUsingCachedReports] = useState(false);
  const [cashflowYear, setCashflowYear] = useState(() => getCurrentFinanceYear(user));
  const [cashflowGrouping, setCashflowGrouping] = useState('quarterly');
  const [cashflowPropertyFilter, setCashflowPropertyFilter] = useState('all');
  const [financeSubTab, setFinanceSubTab] = useState('transactions');
  const [summary, setSummary] = useState([]);
  const [transactionSearchInput, setTransactionSearchInput] = useState('');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [summaryOffset, setSummaryOffset] = useState(0);
  const [summaryHasMore, setSummaryHasMore] = useState(false);
  const [summaryLoadingMore, setSummaryLoadingMore] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsExpenseGroupIndex, setAnalyticsExpenseGroupIndex] = useState(0);
  const [trialBalance, setTrialBalance] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [remoteCategoryOptions, setRemoteCategoryOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [showSimpleForm, setShowSimpleForm] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [journalEditTarget, setJournalEditTarget] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteApplyRecurring, setDeleteApplyRecurring] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editApplyRecurring, setEditApplyRecurring] = useState(false);
  const [categorySavingId, setCategorySavingId] = useState(null);
  const [simpleSubmitting, setSimpleSubmitting] = useState(false);
  const [journalSubmitting, setJournalSubmitting] = useState(false);
  const [journalFieldErrors, setJournalFieldErrors] = useState({});
  const [simpleForm, setSimpleForm] = useState({
    property_id: '',
    type: 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [editForm, setEditForm] = useState({
    property_id: '',
    type: 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('monthly');
  const [recurringCount, setRecurringCount] = useState(3);
  const [journalForm, setJournalForm] = useState({
    property_id: '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
  });
  const [journalLines, setJournalLines] = useState([createEmptyJournalLine(), createEmptyJournalLine()]);
  const [importFile, setImportFile] = useState(null);
  const [importPropertyId, setImportPropertyId] = useState('');
  const [importHeaders, setImportHeaders] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importPreviewEntries, setImportPreviewEntries] = useState([]);
  const [importCategoryOverrides, setImportCategoryOverrides] = useState({});
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importStage, setImportStage] = useState('select');
  const [importMappingErrors, setImportMappingErrors] = useState([]);
  const prefersReducedMotion = useMemo(
    () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false),
    [],
  );
  const chartAnimationEnabled = isOnline && !prefersReducedMotion;
  const simpleDescriptionHelper = useMemo(
    () => getDescriptionHelperText(simpleForm.type, simpleForm.category),
    [simpleForm.category, simpleForm.type],
  );
  const editDescriptionHelper = useMemo(
    () => getDescriptionHelperText(editForm.type, editForm.category),
    [editForm.category, editForm.type],
  );

  const fallbackCategoryOptions = useMemo(
    () => ({
      income: isCompany ? CT600_INCOME_CATEGORIES : INCOME_CATEGORIES,
      expense: isCompany ? CT600_EXPENSE_CATEGORIES : EXPENSE_CATEGORIES,
    }),
    [isCompany],
  );

  const categoryOptions = remoteCategoryOptions || fallbackCategoryOptions;

  const fallbackAccountOptions = useMemo(
    () => (isCompany
      ? ACCOUNT_OPTIONS
      : ACCOUNT_OPTIONS.filter((account) => !COMPANY_ONLY_ACCOUNT_VALUES.has(account.value))),
    [isCompany],
  );

  const activeAccountOptions = accountOptions.length ? accountOptions : fallbackAccountOptions;

  const currentPropertyParam = useMemo(
    () => (cashflowPropertyFilter !== 'all' ? { property_id: cashflowPropertyFilter } : {}),
    [cashflowPropertyFilter],
  );

  const analyticsExpenseGroups = useMemo(
    () => analytics?.expenses_by_category_groups || [],
    [analytics?.expenses_by_category_groups],
  );

  const analyticsSelectedExpenseGroup = useMemo(() => {
    if (!analyticsExpenseGroups.length) return null;
    const safeIndex = Math.max(0, Math.min(analyticsExpenseGroupIndex, analyticsExpenseGroups.length - 1));
    return analyticsExpenseGroups[safeIndex] || null;
  }, [analyticsExpenseGroupIndex, analyticsExpenseGroups]);

  const analyticsExpenseCategories = useMemo(() => {
    const apiItems = analyticsSelectedExpenseGroup?.expenses_by_category || analytics?.expenses_by_category || [];
    const totalsByCategory = new Map(
      apiItems.map((item) => [item.category, Number(item.total || 0)]),
    );
    const activeExpenseCategories = categoryOptions.expense;
    const labelForCategory = (item) => {
      if (isCompany) return item.label;
      return SA105_LABEL_BY_CATEGORY[item.value] || item.label;
    };

    const mapped = activeExpenseCategories.map((category) => ({
      category: category.value,
      label: labelForCategory(category),
      total: Number(totalsByCategory.get(category.value) || 0),
      color: getExpenseCategoryColor(category.value, activeExpenseCategories),
    }));

    const knownCategories = new Set(activeExpenseCategories.map((category) => category.value));
    const extras = apiItems
      .filter((item) => !knownCategories.has(item.category))
      .map((item) => ({
        category: item.category,
        label: isCompany ? (item.label || item.category) : (SA105_LABEL_BY_CATEGORY[item.category] || item.label || item.category),
        total: Number(item.total || 0),
        color: getExpenseCategoryColor(item.category, activeExpenseCategories),
      }));

    return [...mapped, ...extras];
  }, [analytics?.expenses_by_category, analyticsSelectedExpenseGroup?.expenses_by_category, categoryOptions.expense, isCompany]);

  const analyticsGroupingLabel = useMemo(
    () => getGroupingLabel(analytics?.grouping || cashflowGrouping),
    [analytics?.grouping, cashflowGrouping],
  );

  const analyticsExpensePeriodLabel = useMemo(() => {
    if (analyticsSelectedExpenseGroup?.period) return analyticsSelectedExpenseGroup.period;
    return analytics?.expenses_by_category_period || `${analyticsGroupingLabel} period`;
  }, [analytics?.expenses_by_category_period, analyticsGroupingLabel, analyticsSelectedExpenseGroup?.period]);

  const analyticsSelectedPeriodTotals = useMemo(() => {
    const periodRows = analytics?.monthly_cashflow || [];
    const matched = periodRows.find((row) => row.month_label === analyticsExpensePeriodLabel);
    if (matched) {
      return {
        income: Number(matched.income || 0),
        expenses: Number(matched.expenses || 0),
        net: Number(matched.net || 0),
      };
    }
    const expenses = analyticsExpenseCategories.reduce((sum, item) => sum + Number(item.total || 0), 0);
    return {
      income: 0,
      expenses,
      net: -expenses,
    };
  }, [analytics?.monthly_cashflow, analyticsExpenseCategories, analyticsExpensePeriodLabel]);

  const analyticsCanSwitchExpensePeriod = useMemo(
    () => cashflowGrouping !== 'yearly' && analyticsExpenseGroups.length > 1,
    [analyticsExpenseGroups.length, cashflowGrouping],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const onViewportChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onViewportChange);
      return () => mediaQuery.removeEventListener('change', onViewportChange);
    }
    mediaQuery.addListener(onViewportChange);
    return () => mediaQuery.removeListener(onViewportChange);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    setAccountOptions([]);
    setRemoteCategoryOptions(null);
    Promise.all([
      axios.get(`${API_URL}/api/cashflow/accounts`),
      axios.get(`${API_URL}/api/cashflow/categories`),
    ])
      .then(([accountsResponse, categoriesResponse]) => {
        if (cancelled) return;
        setAccountOptions(accountsResponse.data || []);
        const categories = categoriesResponse.data;
        setRemoteCategoryOptions(categories?.income?.length && categories?.expense?.length ? categories : null);
      })
      .catch(() => {
        if (!cancelled) {
          setAccountOptions([]);
          setRemoteCategoryOptions(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, isCompany]);

  useEffect(() => {
    if (!analyticsExpenseGroups.length) {
      setAnalyticsExpenseGroupIndex(0);
      return;
    }
    const targetPeriod = analytics?.expenses_by_category_period;
    const matchedIndex = targetPeriod
      ? analyticsExpenseGroups.findIndex((group) => group.period === targetPeriod)
      : -1;
    if (matchedIndex >= 0) {
      setAnalyticsExpenseGroupIndex(matchedIndex);
      return;
    }
    const lastWithData = analyticsExpenseGroups
      .map((group, index) => ({ index, hasData: (group.expenses_by_category || []).length > 0 }))
      .filter((item) => item.hasData)
      .map((item) => item.index)
      .pop();
    // Default to the current (most recent) quarter even if it has no expense data yet,
    // so the UI always opens on the live period rather than a past one.
    setAnalyticsExpenseGroupIndex(analyticsExpenseGroups.length - 1);
  }, [analytics?.expenses_by_category_period, analyticsExpenseGroups]);

  const mergeSummaryGroups = useCallback((existing, incoming) => {
    const map = new Map(existing.map((group) => [group.period, { ...group, rows: [...(group.rows || [])] }]));
    for (const group of incoming) {
      const current = map.get(group.period);
      if (!current) {
        map.set(group.period, { ...group, rows: [...(group.rows || [])] });
        continue;
      }
      const mergedRows = [...(current.rows || []), ...(group.rows || [])];
      map.set(group.period, {
        ...current,
        rows: mergedRows,
        income_total: Number(current.income_total || 0) + Number(group.income_total || 0),
        expenses_total: Number(current.expenses_total || 0) + Number(group.expenses_total || 0),
        net: Number(current.net || 0) + Number(group.net || 0),
      });
    }
    return Array.from(map.values());
  }, []);

  const buildCacheKey = useCallback((section, extra = {}) => {
    const payload = {
      section,
      user_id: user?.id || 'current',
      ownership_type: user?.ownership_type || 'individual',
      year: cashflowYear,
      grouping: cashflowGrouping,
      property: cashflowPropertyFilter,
      ...extra,
    };
    return `finance:${FINANCE_CACHE_VERSION}:${JSON.stringify(payload)}`;
  }, [cashflowGrouping, cashflowPropertyFilter, cashflowYear, user?.id, user?.ownership_type]);

  const writeCache = useCallback((key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ data, updatedAt: Date.now() }));
    } catch {
      // ignore cache write issues
    }
  }, []);

  const readCache = useCallback((key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.data ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setCashflowYear(getCurrentFinanceYear(user));
    setSummary([]);
    setAnalytics(null);
    setTrialBalance(null);
    setProfitLoss(null);
    setBalanceSheet(null);
    setUsingCachedSummary(false);
    setUsingCachedAnalytics(false);
    setUsingCachedReports(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ownership_type, user?.company_year_end]);

  const requireOnline = useCallback((actionLabel) => {
    if (isOnline) return true;
    toast.error(`${actionLabel} requires an internet connection`);
    return false;
  }, [isOnline]);

  const fetchSummary = useCallback(async ({ append = false, offset = 0 } = {}) => {
    if (!isActive) return;
    if (append) {
      setSummaryLoadingMore(true);
    } else {
      setLoading(true);
    }

    if (!isOnline && !append) {
      const cacheKey = buildCacheKey('summary', { search: transactionSearch });
      const cached = readCache(cacheKey);
      if (cached?.groups) {
        setSummary(cached.groups);
        setSummaryOffset(Number(cached.next_offset || 0));
        setSummaryHasMore(Boolean(cached.has_more));
        setUsingCachedSummary(true);
      }
      setLoading(false);
      return;
    }

    try {
      const requestOffset = append ? offset : 0;
      const response = await axios.get(`${API_URL}/api/cashflow/summary`, {
        timeout: FINANCE_REQUEST_TIMEOUT_MS,
        params: {
          year: cashflowYear,
          grouping: cashflowGrouping,
          search: transactionSearch || undefined,
          include_meta: true,
          offset: requestOffset,
          limit: 50,
          ...currentPropertyParam,
        },
      });
      const payload = response.data || {};
      const groups = payload.groups || [];
      if (append) {
        setSummary((current) => mergeSummaryGroups(current, groups));
      } else {
        setSummary(groups);
      }
      setSummaryOffset(Number(payload.next_offset || 0));
      setSummaryHasMore(Boolean(payload.has_more));
      if (!append) {
        const cacheKey = buildCacheKey('summary', { search: transactionSearch });
        writeCache(cacheKey, payload);
      }
      setUsingCachedSummary(false);
    } catch {
      if (!append) {
        const cacheKey = buildCacheKey('summary', { search: transactionSearch });
        const cached = readCache(cacheKey);
        if (cached?.groups) {
          setSummary(cached.groups);
          setSummaryOffset(Number(cached.next_offset || 0));
          setSummaryHasMore(Boolean(cached.has_more));
          setUsingCachedSummary(true);
          toast.error('Offline: showing cached transactions');
        } else {
          toast.error('Failed to load transactions');
        }
      } else {
        toast.error('Failed to load more transactions');
      }
    } finally {
      if (append) {
        setSummaryLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [
    buildCacheKey,
    cashflowGrouping,
    cashflowYear,
    currentPropertyParam,
    isActive,
    isOnline,
    mergeSummaryGroups,
    readCache,
    transactionSearch,
    writeCache,
  ]);

  const refreshTransactionsForDate = useCallback((dateValue) => {
    const targetYear = getFinanceYearForDate(user, dateValue);
    if (targetYear !== cashflowYear) {
      setCashflowYear(targetYear);
      return;
    }
    fetchSummary({ append: false });
  }, [cashflowYear, fetchSummary, user]);

  const fetchReports = useCallback(async () => {
    if (!isActive) return;
    setReportsLoading(true);

    if (!isOnline) {
      const cached = readCache(buildCacheKey('reports'));
      if (cached?.trialBalance || cached?.profitLoss || cached?.balanceSheet) {
        setTrialBalance(cached.trialBalance || null);
        setProfitLoss(cached.profitLoss || null);
        setBalanceSheet(cached.balanceSheet || null);
        setUsingCachedReports(true);
      }
      setReportsLoading(false);
      return;
    }

    try {
      const params = { year: cashflowYear, ...currentPropertyParam };
      const [trialRes, pnlRes, balanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/trial-balance`, { params, timeout: FINANCE_REQUEST_TIMEOUT_MS }),
        axios.get(`${API_URL}/api/reports/pnl`, { params, timeout: FINANCE_REQUEST_TIMEOUT_MS }),
        axios.get(`${API_URL}/api/reports/balance-sheet`, { params, timeout: FINANCE_REQUEST_TIMEOUT_MS }),
      ]);
      setTrialBalance(trialRes.data);
      setProfitLoss(pnlRes.data);
      setBalanceSheet(balanceRes.data);
      writeCache(buildCacheKey('reports'), {
        trialBalance: trialRes.data,
        profitLoss: pnlRes.data,
        balanceSheet: balanceRes.data,
      });
      setUsingCachedReports(false);
    } catch {
      const cached = readCache(buildCacheKey('reports'));
      if (cached?.trialBalance || cached?.profitLoss || cached?.balanceSheet) {
        setTrialBalance(cached.trialBalance || null);
        setProfitLoss(cached.profitLoss || null);
        setBalanceSheet(cached.balanceSheet || null);
        setUsingCachedReports(true);
        toast.error('Offline: showing cached reports');
      } else {
        toast.error('Failed to load reports');
      }
    } finally {
      setReportsLoading(false);
    }
  }, [buildCacheKey, cashflowYear, currentPropertyParam, isActive, isOnline, readCache, writeCache]);

  const fetchAnalytics = useCallback(async () => {
    if (!isActive) return;
    setAnalyticsLoading(true);

    if (!isOnline) {
      const cached = readCache(buildCacheKey('analytics'));
      if (cached) {
        setAnalytics(cached);
        setUsingCachedAnalytics(true);
      }
      setAnalyticsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/cashflow/analytics`, {
        timeout: FINANCE_REQUEST_TIMEOUT_MS,
        params: {
          year: cashflowYear,
          grouping: cashflowGrouping,
          ...currentPropertyParam,
        },
      });
      setAnalytics(response.data || null);
      writeCache(buildCacheKey('analytics'), response.data || null);
      setUsingCachedAnalytics(false);
    } catch {
      const cached = readCache(buildCacheKey('analytics'));
      if (cached) {
        setAnalytics(cached);
        setUsingCachedAnalytics(true);
        toast.error('Offline: showing cached analytics');
      } else {
        toast.error('Failed to load analytics');
        setAnalytics(null);
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [buildCacheKey, cashflowGrouping, cashflowYear, currentPropertyParam, isActive, isOnline, readCache, writeCache]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setTransactionSearch(transactionSearchInput.trim());
    }, 250);
    return () => clearTimeout(handle);
  }, [transactionSearchInput]);

  useEffect(() => {
    if (!isActive) return;
    if (financeSubTab === 'transactions') {
      fetchSummary({ append: false });
      return;
    }
    if (financeSubTab === 'analytics') {
      fetchAnalytics();
      return;
    }
    if (financeSubTab === 'reports') {
      fetchReports();
    }
  }, [fetchAnalytics, fetchReports, fetchSummary, financeSubTab, isActive]);

  const resetImportState = () => {
    setImportHeaders([]);
    setImportMapping({});
    setImportPreviewRows([]);
    setImportPreviewEntries([]);
    setImportCategoryOverrides({});
    setImportStage('select');
  };

  const handleSimpleSubmit = async (event) => {
    event.preventDefault();
    if (!requireOnline('Saving transactions')) return;
    if (!simpleForm.property_id) return toast.error('Select a property');
    if (!simpleForm.category) return toast.error('Select a category');
    if (!simpleForm.amount || Number(simpleForm.amount) <= 0) return toast.error('Amount must be greater than zero');
    if (!simpleForm.description.trim()) return toast.error('Description is required');
    if (!simpleForm.date) return toast.error('Date is required');

    const payload = {
      property_id: simpleForm.property_id,
      type: simpleForm.type,
      category: simpleForm.category,
      amount: Number(simpleForm.amount),
      description: simpleForm.description.trim(),
      date: simpleForm.date,
    };
    const dates = recurringEnabled
      ? buildRecurringDates(simpleForm.date, recurringFrequency, recurringCount)
      : [simpleForm.date];
    const recurringGroupId = recurringEnabled ? crypto.randomUUID() : null;

    setSimpleSubmitting(true);
    try {
      await Promise.all(
        dates.map((date) => axios.post(`${API_URL}/api/cashflow/entries`, {
          ...payload,
          date,
          recurring_group_id: recurringGroupId,
        })),
      );
      toast.success(dates.length > 1 ? `${dates.length} transactions saved` : 'Transaction recorded');
      setSimpleForm((current) => ({
        ...current,
        amount: '',
        category: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
      }));
      setRecurringEnabled(false);
      setShowSimpleForm(false);
      refreshTransactionsForDate(payload.date);
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save transaction');
    } finally {
      setSimpleSubmitting(false);
    }
  };

  const journalTotals = useMemo(() => {
    const debit = journalLines.reduce((total, line) => total + (Number(line.debit) || 0), 0);
    const credit = journalLines.reduce((total, line) => total + (Number(line.credit) || 0), 0);
    return {
      debit,
      credit,
      balanced: Math.abs(debit - credit) <= 0.009,
    };
  }, [journalLines]);

  const handleJournalSubmit = async (event) => {
    event.preventDefault();
    if (!requireOnline('Saving journals')) return;
    const nextErrors = {};
    const lineErrors = {};
    if (!journalForm.property_id) nextErrors.property_id = 'Property is required';
    if (!journalForm.date) nextErrors.date = 'Date is required';
    if (!journalForm.description.trim()) nextErrors.description = 'Description is required';
    if (journalLines.length < 2) nextErrors.lines_count = 'Journal needs at least two lines';
    for (const line of journalLines) {
      const debit = Number(line.debit) || 0;
      const credit = Number(line.credit) || 0;
      if (!line.account) {
        lineErrors[line.id] = 'Account is required';
        continue;
      }
      if ((debit <= 0 && credit <= 0) || (debit > 0 && credit > 0)) {
        lineErrors[line.id] = 'Each line must have either a debit or a credit';
      }
    }
    if (Object.keys(lineErrors).length) nextErrors.lines = lineErrors;
    if (!journalTotals.balanced) nextErrors.totals = 'Journal must balance before submit';
    setJournalFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error('Fix journal validation errors before saving');
      return;
    }

    setJournalSubmitting(true);
    try {
      const payload = {
        property_id: journalForm.property_id,
        description: journalForm.description.trim(),
        date: journalForm.date,
        lines: journalLines.map((line) => ({
          account: line.account,
          debit: Number(line.debit) || 0,
          credit: Number(line.credit) || 0,
          description: line.description.trim(),
        })),
      };
      if (journalEditTarget?.entry_id) {
        await axios.patch(`${API_URL}/api/cashflow/entries/${journalEditTarget.entry_id}`, payload);
        toast.success('Journal entry updated');
      } else {
        await axios.post(`${API_URL}/api/cashflow/entries`, payload);
        toast.success('Journal entry recorded');
      }
      setJournalForm({
        property_id: '',
        date: new Date().toISOString().slice(0, 10),
        description: '',
      });
      setJournalLines([createEmptyJournalLine(), createEmptyJournalLine()]);
      setJournalEditTarget(null);
      setJournalFieldErrors({});
      setShowJournalForm(false);
      refreshTransactionsForDate(payload.date);
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save journal');
    } finally {
      setJournalSubmitting(false);
    }
  };

  const toggleExpanded = (entryId) => {
    setExpandedRows((current) => ({ ...current, [entryId]: !current[entryId] }));
  };

  const startEdit = (row) => {
    if (row.is_journal) {
      setShowSimpleForm(false);
      setShowJournalForm(true);
      setFinanceSubTab('transactions');
      setEditTarget(null);
      setJournalEditTarget(row);
      setJournalForm({
        property_id: row.property_id || '',
        date: row.date || new Date().toISOString().slice(0, 10),
        description: row.description || '',
      });
      const nextLines = (row.lines || []).map((line) => ({
        id: crypto.randomUUID(),
        account: line.account || '',
        debit: line.debit ? String(line.debit) : '',
        credit: line.credit ? String(line.credit) : '',
        description: line.description || '',
      }));
      setJournalLines(nextLines.length >= 2 ? nextLines : [createEmptyJournalLine(), createEmptyJournalLine()]);
      return;
    }
    setShowSimpleForm(false);
    setShowJournalForm(false);
    setEditTarget(row);
    setEditApplyRecurring(false);
    setEditForm({
      property_id: row.property_id || '',
      type: row.type || 'expense',
      amount: String(Number(row.amount || 0).toFixed(2)),
      category: row.category || '',
      description: row.description || '',
      date: row.date || new Date().toISOString().slice(0, 10),
    });
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditApplyRecurring(false);
  };

  const resetEditForm = () => {
    if (!editTarget) return;
    setEditForm({
      property_id: editTarget.property_id || '',
      type: editTarget.type || 'expense',
      amount: String(Number(editTarget.amount || 0).toFixed(2)),
      category: editTarget.category || '',
      description: editTarget.description || '',
      date: editTarget.date || new Date().toISOString().slice(0, 10),
    });
    setEditApplyRecurring(false);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!requireOnline('Updating transaction')) return;
    if (!editTarget) return;
    if (!editForm.property_id) return toast.error('Select a property');
    if (!editForm.category) return toast.error('Select a category');
    if (!editForm.amount || Number(editForm.amount) <= 0) return toast.error('Amount must be greater than zero');
    if (!editForm.description.trim()) return toast.error('Description is required');
    if (!editForm.date) return toast.error('Date is required');

    setEditSubmitting(true);
    try {
      await axios.patch(`${API_URL}/api/cashflow/entries/${editTarget.entry_id}`, {
        property_id: editForm.property_id,
        type: editForm.type,
        category: editForm.category,
        amount: Number(editForm.amount),
        description: editForm.description.trim(),
        date: editForm.date,
        apply_to_recurring: Boolean(editApplyRecurring && editTarget.recurring_group_id),
      });
      toast.success(editApplyRecurring && editTarget.recurring_group_id ? 'Recurring transactions updated' : 'Transaction updated');
      cancelEdit();
      refreshTransactionsForDate(editForm.date);
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update transaction');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!requireOnline('Deleting transaction')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/cashflow/entries/${deleteTarget.entry_id}`, {
        params: {
          apply_to_recurring: Boolean(deleteApplyRecurring && deleteTarget.recurring_group_id),
        },
      });
      toast.success('Entry deleted');
      setDeleteTarget(null);
      setDeleteApplyRecurring(false);
      fetchSummary({ append: false });
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch {
      toast.error('Could not delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const handleInlineCategoryChange = async (entryId, nextCategory) => {
    if (!requireOnline('Updating category')) return;
    setCategorySavingId(entryId);
    try {
      await axios.patch(`${API_URL}/api/cashflow/entries/${entryId}/category`, { category: nextCategory });
      fetchSummary({ append: false });
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
      toast.success('Category updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update category');
    } finally {
      setCategorySavingId(null);
    }
  };

  const buildImportFormData = (commit = false, includeMapping = false) => {
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('property_id', importPropertyId);
    if (includeMapping) {
      formData.append('mapping_json', JSON.stringify(importMapping));
      formData.append('commit', commit ? 'true' : 'false');
      if (commit && Object.keys(importCategoryOverrides).length > 0) {
        formData.append('category_overrides_json', JSON.stringify(importCategoryOverrides));
      }
    }
    return formData;
  };

  const validateImportMapping = () => {
    const missing = [];
    if (!importMapping.date) missing.push('Date mapping is required');
    if (!importMapping.description) missing.push('Description mapping is required');
    if (!importMapping.amount && !importMapping.paid_in && !importMapping.paid_out) {
      missing.push('Map Amount, or both Paid in / Paid out fields');
    }
    setImportMappingErrors(missing);
    if (missing.length > 0) {
      toast.error('Complete required CSV mappings before continuing');
      return false;
    }
    return true;
  };

  const detectImportColumns = async () => {
    if (!requireOnline('Detecting CSV columns')) return;
    if (!importFile) return toast.error('Choose a CSV file');
    if (!importPropertyId) return toast.error('Select a property');
    setImportMappingErrors([]);
    setImportSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/cashflow/import`, buildImportFormData(false, false));
      setImportHeaders(response.data.headers || []);
      setImportMapping(response.data.suggested_mapping || {});
      setImportPreviewRows(response.data.preview_rows || []);
      setImportPreviewEntries([]);
      setImportStage('map');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not read CSV');
    } finally {
      setImportSubmitting(false);
    }
  };

  const previewImport = async () => {
    if (!requireOnline('Previewing CSV import')) return;
    if (!validateImportMapping()) return;
    setImportSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/cashflow/import`, buildImportFormData(false, true));
      const entries = response.data.preview_entries || [];
      // Pre-populate overrides from suggested_category so the dropdown starts there.
      const initialOverrides = {};
      entries.forEach((entry, idx) => {
        if (entry.suggested_category && entry.suggested_category !== entry.category) {
          initialOverrides[String(idx)] = entry.suggested_category;
        }
      });
      setImportCategoryOverrides(initialOverrides);
      setImportPreviewEntries(entries);
      setImportStage('preview');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not preview CSV');
    } finally {
      setImportSubmitting(false);
    }
  };

  const commitImport = async () => {
    if (!requireOnline('Importing CSV')) return;
    if (!validateImportMapping()) return;
    setImportSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/cashflow/import`, buildImportFormData(true, true));
      toast.success(`${response.data.created_count || 0} entries imported`);
      setImportFile(null);
      setImportPropertyId('');
      resetImportState();
      fetchSummary({ append: false });
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not import CSV');
    } finally {
      setImportSubmitting(false);
    }
  };

  const exportUrl = useMemo(() => {
    const params = new URLSearchParams({ year: String(cashflowYear), grouping: cashflowGrouping });
    if (cashflowPropertyFilter !== 'all') params.set('property_id', cashflowPropertyFilter);
    return `${API_URL}/api/cashflow/export?${params.toString()}`;
  }, [cashflowGrouping, cashflowPropertyFilter, cashflowYear]);

  const reportsPdfUrls = useMemo(() => {
    const params = new URLSearchParams({ year: String(cashflowYear) });
    if (cashflowPropertyFilter !== 'all') params.set('property_id', cashflowPropertyFilter);
    return {
      pnl: `${API_URL}/api/reports/pnl/pdf?${params.toString()}`,
      balanceSheet: `${API_URL}/api/reports/balance-sheet/pdf?${params.toString()}`,
      trialBalance: `${API_URL}/api/reports/trial-balance/pdf?${params.toString()}`,
    };
  }, [cashflowPropertyFilter, cashflowYear]);

  const taxSummaryPdfUrl = useMemo(() => {
    const params = new URLSearchParams({ year: String(cashflowYear), grouping: cashflowGrouping });
    if (cashflowPropertyFilter !== 'all') params.set('property_id', cashflowPropertyFilter);
    return `${API_URL}/api/cashflow/tax-summary/pdf?${params.toString()}`;
  }, [cashflowGrouping, cashflowPropertyFilter, cashflowYear]);

  const taxSummaryCsvUrl = useMemo(() => {
    const params = new URLSearchParams({ year: String(cashflowYear), grouping: cashflowGrouping });
    if (cashflowPropertyFilter !== 'all') params.set('property_id', cashflowPropertyFilter);
    return `${API_URL}/api/cashflow/tax-summary/csv?${params.toString()}`;
  }, [cashflowGrouping, cashflowPropertyFilter, cashflowYear]);

  const balanceSheetCashBalance = useMemo(() => {
    const cashRow = (balanceSheet?.assets || []).find((row) => row.account === 'cash');
    return Number(cashRow?.balance || 0);
  }, [balanceSheet]);

  const adjustCashflowYear = useCallback((delta) => {
    setCashflowYear((previousYear) => {
      const baseYear = Number(previousYear) || getCurrentTaxYear();
      return baseYear + delta;
    });
  }, []);

  const handleCashflowYearChange = useCallback((event) => {
    const nextYear = Number(event.target.value);
    if (Number.isNaN(nextYear)) return;
    setCashflowYear(nextYear || getCurrentTaxYear());
  }, []);

  const handleSelectTransaction = useCallback((entryId) => {
    setSelectedTransactions((current) => {
      const next = new Set(current);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  }, []);

  const handleSelectAllTransactions = useCallback(() => {
    if (selectedTransactions.size === 0) {
      const allIds = new Set();
      summary.forEach((group) => {
        (group.rows || []).forEach((row) => {
          allIds.add(row.entry_id);
        });
      });
      setSelectedTransactions(allIds);
    } else {
      setSelectedTransactions(new Set());
    }
  }, [selectedTransactions.size, summary]);

  const handleBulkDelete = async () => {
    if (selectedTransactions.size === 0) return;
    if (!requireOnline('Deleting transactions')) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        Array.from(selectedTransactions).map((entryId) =>
          axios.delete(`${API_URL}/api/cashflow/entries/${entryId}`, {
            params: { apply_to_recurring: false },
          })
        )
      );
      toast.success(`${selectedTransactions.size} transaction(s) deleted`);
      setSelectedTransactions(new Set());
      fetchSummary({ append: false });
      if (financeSubTab === 'analytics') fetchAnalytics();
      if (financeSubTab === 'reports') fetchReports();
    } catch {
      toast.error('Error deleting transactions');
    } finally {
      setBulkDeleting(false);
    }
  };

  const clampRecurringCount = useCallback((value) => Math.max(2, Math.min(60, value)), []);

  const adjustRecurringCount = useCallback((delta) => {
    setRecurringCount((currentCount) => clampRecurringCount((Number(currentCount) || 2) + delta));
  }, [clampRecurringCount]);

  const handleRecurringCountChange = useCallback((event) => {
    const nextCount = Number(event.target.value);
    if (Number.isNaN(nextCount)) return;
    setRecurringCount(clampRecurringCount(nextCount || 2));
  }, [clampRecurringCount]);

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-w-0 max-w-full space-y-6">
      {(!isOnline || usingCachedSummary || usingCachedAnalytics || usingCachedReports) && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="py-3 text-sm text-amber-900">
            {!isOnline
              ? 'Offline mode: showing cached finance data where available.'
              : 'Showing cached data while reconnecting.'}
          </CardContent>
        </Card>
      )}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <Label>{isCompany ? 'Accounting Period' : 'Tax year'}</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 sm:hidden"
                onClick={() => adjustCashflowYear(-1)}
                aria-label={isCompany ? 'Decrease accounting period' : 'Decrease tax year'}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                value={cashflowYear}
                onChange={handleCashflowYearChange}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 sm:hidden"
                onClick={() => adjustCashflowYear(1)}
                aria-label={isCompany ? 'Increase accounting period' : 'Increase tax year'}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Grouping</Label>
            <Select value={cashflowGrouping} onValueChange={setCashflowGrouping}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Property</Label>
            <Select value={cashflowPropertyFilter} onValueChange={setCashflowPropertyFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={showJournalForm ? 'outline' : 'secondary'}
            onClick={() => {
              if (showJournalForm) {
                setShowJournalForm(false);
                setJournalEditTarget(null);
                setJournalForm({
                  property_id: '',
                  date: new Date().toISOString().slice(0, 10),
                  description: '',
                });
                setJournalLines([createEmptyJournalLine(), createEmptyJournalLine()]);
              } else {
                setShowJournalForm(true);
              }
              setShowSimpleForm(false);
              setFinanceSubTab('transactions');
            }}
          >
            {showJournalForm ? <><X className="mr-2 h-4 w-4" />Cancel</> : <><BookOpen className="mr-2 h-4 w-4" />Journal Entry</>}
          </Button>
          <Button variant={showSimpleForm ? 'outline' : 'default'} onClick={() => { setShowSimpleForm((current) => !current); setShowJournalForm(false); setFinanceSubTab('transactions'); }}>
            {showSimpleForm ? <><X className="mr-2 h-4 w-4" />Cancel</> : <><Plus className="mr-2 h-4 w-4" />Record Transaction</>}
          </Button>
          <Button variant="outline" onClick={() => setFinanceSubTab('import')}>
            <Upload className="mr-2 h-4 w-4" />Import CSV
          </Button>
        </div>
      </div>

      <div className="inline-flex h-auto min-h-9 w-full min-w-0 max-w-full items-center justify-start gap-1 overflow-x-auto scrollbar-hide rounded-lg bg-muted p-1 text-muted-foreground sm:overflow-visible">
        {subTabs.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setFinanceSubTab(value)}
            className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all sm:px-4 ${financeSubTab === value ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {financeSubTab === 'transactions' && (
        <div className="min-w-0 space-y-6">
          {showSimpleForm && (
            <Card className="border-royal-200 bg-royal-50/30">
              <CardHeader>
                <CardTitle className="text-base">Simple transaction</CardTitle>
                <CardDescription>Keep the same quick workflow. The backend stores the journal lines for you.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 flex gap-1 rounded-md bg-muted p-1 w-fit">
                  {['expense', 'income'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSimpleForm((current) => ({ ...current, type: value, category: '' }))}
                      className={`rounded px-4 py-1.5 text-sm font-medium ${simpleForm.type === value ? value === 'income' ? 'bg-green-600 text-white' : 'bg-red-600 text-white' : 'text-muted-foreground'}`}
                    >
                      {value === 'income' ? 'Income' : 'Expense'}
                    </button>
                  ))}
                </div>
                <form className="space-y-4" onSubmit={handleSimpleSubmit}>
                  <div className="grid gap-4 lg:grid-cols-4">
                    <div className="space-y-1">
                      <Label>Property</Label>
                      <Select value={simpleForm.property_id} onValueChange={(value) => setSimpleForm((current) => ({ ...current, property_id: value }))}>
                        <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>
                          {properties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Category</Label>
                      <Select value={simpleForm.category} onValueChange={(value) => setSimpleForm((current) => ({ ...current, category: value }))}>
                        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>
                          {categoryOptions[simpleForm.type].map((category) => (
                            <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Amount</Label>
                      <Input type="number" min="0.01" step="0.01" value={simpleForm.amount} onChange={(event) => setSimpleForm((current) => ({ ...current, amount: event.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Date</Label>
                      <Input type="date" value={simpleForm.date} onChange={(event) => setSimpleForm((current) => ({ ...current, date: event.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Description</Label>
                    <Input value={simpleForm.description} onChange={(event) => setSimpleForm((current) => ({ ...current, description: event.target.value }))} maxLength={500} />
                    <p className="text-xs text-muted-foreground">{simpleDescriptionHelper}</p>
                  </div>
                  <div className="space-y-3 rounded-lg border border-border p-3">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" checked={recurringEnabled} onChange={(event) => setRecurringEnabled(event.target.checked)} />
                      Recurring transaction
                    </label>
                    {recurringEnabled && (
                      <div className="flex flex-wrap gap-3">
                        <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0 sm:hidden"
                            onClick={() => adjustRecurringCount(-1)}
                            aria-label="Decrease recurring periods"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Input
                            className="w-24"
                            type="number"
                            inputMode="numeric"
                            min="2"
                            max="60"
                            step="1"
                            value={recurringCount}
                            onChange={handleRecurringCountChange}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0 sm:hidden"
                            onClick={() => adjustRecurringCount(1)}
                            aria-label="Increase recurring periods"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={simpleSubmitting}>
                      {simpleSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                      Save transaction
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {showJournalForm && (
            <Card className="border-amber-200 bg-amber-50/20">
              <CardHeader>
                <CardTitle className="text-base">{journalEditTarget ? 'Edit ledger journal' : 'Ledger journal'}</CardTitle>
                <CardDescription>
                  {journalEditTarget ? 'Update journal lines and save changes.' : 'Enter accounts directly. Journals must balance before submission.'}
                </CardDescription>
                {journalEditTarget && <p className="text-sm font-medium text-amber-700">Editing journal entry</p>}
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleJournalSubmit}>
                  <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-1">
                      <Label>Property</Label>
                      <Select value={journalForm.property_id} onValueChange={(value) => {
                        setJournalForm((current) => ({ ...current, property_id: value }));
                        setJournalFieldErrors((current) => ({ ...current, property_id: undefined }));
                      }}>
                        <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>
                          {properties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {journalFieldErrors.property_id && <p className="text-xs text-red-600">{journalFieldErrors.property_id}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label>Date</Label>
                      <Input type="date" value={journalForm.date} onChange={(event) => {
                        setJournalForm((current) => ({ ...current, date: event.target.value }));
                        setJournalFieldErrors((current) => ({ ...current, date: undefined }));
                      }} />
                      {journalFieldErrors.date && <p className="text-xs text-red-600">{journalFieldErrors.date}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label>Description</Label>
                      <Input value={journalForm.description} onChange={(event) => {
                        setJournalForm((current) => ({ ...current, description: event.target.value }));
                        setJournalFieldErrors((current) => ({ ...current, description: undefined }));
                      }} maxLength={500} />
                      {journalFieldErrors.description && <p className="text-xs text-red-600">{journalFieldErrors.description}</p>}
                    </div>
                  </div>
                  {journalFieldErrors.lines_count && <p className="text-xs text-red-600">{journalFieldErrors.lines_count}</p>}
                  <div className="space-y-2">
                    <div className="overflow-x-auto pb-1">
                      <div className="min-w-[760px] origin-top-left scale-[0.94] sm:scale-100">
                        <div className="grid grid-cols-[1.2fr_140px_140px_1fr_32px] gap-2 px-1 text-xs font-medium text-muted-foreground">
                          <span>Account</span>
                          <span>Debit</span>
                          <span>Credit</span>
                          <span>Description</span>
                          <span />
                        </div>
                        {journalLines.map((line) => (
                          <div key={line.id} className="grid grid-cols-[1.2fr_140px_140px_1fr_32px] gap-2">
                            <Select value={line.account} onValueChange={(value) => setJournalLines((current) => current.map((item) => item.id === line.id ? { ...item, account: value } : item))}>
                              <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                              <SelectContent>
                                {activeAccountOptions.map((account) => (
                                  <SelectItem key={account.value} value={account.value}>{account.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input type="number" min="0" step="0.01" value={line.debit} onChange={(event) => setJournalLines((current) => current.map((item) => item.id === line.id ? { ...item, debit: event.target.value, credit: event.target.value ? '' : item.credit } : item))} placeholder="0.00" />
                            <Input type="number" min="0" step="0.01" value={line.credit} onChange={(event) => setJournalLines((current) => current.map((item) => item.id === line.id ? { ...item, credit: event.target.value, debit: event.target.value ? '' : item.debit } : item))} placeholder="0.00" />
                            <Input value={line.description} onChange={(event) => setJournalLines((current) => current.map((item) => item.id === line.id ? { ...item, description: event.target.value } : item))} placeholder="Line description" />
                            <button type="button" className="text-muted-foreground hover:text-red-600" disabled={journalLines.length <= 2} onClick={() => setJournalLines((current) => current.filter((item) => item.id !== line.id))}>
                              <X className="h-4 w-4" />
                            </button>
                            {journalFieldErrors.lines?.[line.id] && <p className="col-span-5 text-xs text-red-600">{journalFieldErrors.lines[line.id]}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={`flex flex-wrap items-center justify-between rounded-md border px-3 py-2 text-sm ${journalTotals.balanced ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setJournalLines((current) => [...current, createEmptyJournalLine()])}>
                        <Plus className="mr-2 h-4 w-4" />Add line
                      </Button>
                      <div className="flex gap-4 tabular-nums">
                        <span>Debit {formatCurrency(journalTotals.debit)}</span>
                        <span>Credit {formatCurrency(journalTotals.credit)}</span>
                        <span className={journalTotals.balanced ? 'text-green-700 font-semibold' : 'text-amber-700 font-semibold'}>
                          {journalTotals.balanced ? 'Balanced' : 'Out of balance'}
                        </span>
                      </div>
                    </div>
                    {journalFieldErrors.totals && <p className="text-xs text-red-600">{journalFieldErrors.totals}</p>}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {journalEditTarget && (
                      <>
                        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => startEdit(journalEditTarget)} disabled={journalSubmitting}>Reset</Button>
                        <Button type="button" variant="outline" onClick={() => {
                          setJournalEditTarget(null);
                          setJournalFieldErrors({});
                          setJournalForm({
                            property_id: '',
                            date: new Date().toISOString().slice(0, 10),
                            description: '',
                          });
                          setJournalLines([createEmptyJournalLine(), createEmptyJournalLine()]);
                          setShowJournalForm(false);
                        }} className="w-full sm:w-auto" disabled={journalSubmitting}>Cancel edit</Button>
                      </>
                    )}
                    <Button type="submit" disabled={journalSubmitting} className="w-full bg-amber-700 hover:bg-amber-800 sm:w-auto">
                      {journalSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BookOpen className="mr-2 h-4 w-4" />}
                      {journalEditTarget ? 'Update journal' : 'Save journal'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  value={transactionSearchInput}
                  onChange={(event) => setTransactionSearchInput(event.target.value)}
                  placeholder="Search by description or property"
                  className="sm:max-w-md"
                />
                <div className="flex flex-wrap items-center gap-2">
                  {transactionSearch && (
                    <Button variant="outline" onClick={() => setTransactionSearchInput('')}>Clear search</Button>
                  )}
                  {selectedTransactions.size > 0 && (
                    <>
                      <span className="text-sm text-muted-foreground">{selectedTransactions.size} selected</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                      >
                        {bulkDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                        Delete Selected
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-royal-600" /></div>
          ) : summary.every((group) => (group.rows || []).length === 0) ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">No transactions recorded for the selected period.</CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {summary.filter((group) => (group.rows || []).length > 0).map((group) => (
                <div key={group.period} className="max-w-full overflow-hidden rounded-lg border border-border">
                  <div className="flex flex-col gap-2 bg-muted/60 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="font-semibold">{group.period}</span>
                    <div className="flex flex-wrap gap-4 text-xs font-medium">
                      <span className="text-green-700">Income {formatCurrency(group.income_total)}</span>
                      <span className="text-red-700">Expenses {formatCurrency(group.expenses_total)}</span>
                      <span className={group.net >= 0 ? 'text-green-800' : 'text-red-800'}>Net {formatCurrency(group.net)}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[920px] text-sm">
                      <thead>
                        <tr className="border-b bg-background text-left text-xs text-muted-foreground">
                          <th className="w-10 px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedTransactions.size > 0}
                              onChange={handleSelectAllTransactions}
                              aria-label="Select all transactions"
                            />
                          </th>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Description</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => {
                          const expanded = Boolean(expandedRows[row.entry_id]);
                          const categories = row.type === 'income'
                            ? (isCompany ? CT600_INCOME_CATEGORIES : INCOME_CATEGORIES)
                            : (isCompany ? CT600_EXPENSE_CATEGORIES : EXPENSE_CATEGORIES);
                          return (
                            <Fragment key={row.entry_id}>
                              <tr key={row.entry_id} className="border-b last:border-0">
                                <td className="px-3 py-2 align-top">
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={selectedTransactions.has(row.entry_id)}
                                      onChange={() => handleSelectTransaction(row.entry_id)}
                                      aria-label={`Select transaction ${row.entry_id}`}
                                    />
                                    <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => toggleExpanded(row.entry_id)}>
                                      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-3 py-2 align-top tabular-nums">{row.date}</td>
                                <td className="px-3 py-2 align-top">
                                  <div className="font-medium">{row.description}</div>
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <span>{row.property_name} · {row.is_journal ? 'Journal' : row.label}</span>
                                    {row.recurring_group_id && !row.is_journal && <Badge variant="secondary">Recurring</Badge>}
                                  </div>
                                </td>
                                <td className="px-3 py-2 align-top">
                                  {row.is_journal ? (
                                    <div className="inline-flex items-center gap-2">
                                      <Badge variant="outline">Journal</Badge>
                                      {!isCompany && <Sa105Hint sa105Label={row.sa105_label} />}
                                    </div>
                                  ) : (
                                    <div className="flex items-start gap-2">
                                      <Select value={row.category || ''} onValueChange={(value) => handleInlineCategoryChange(row.entry_id, value)} disabled={categorySavingId === row.entry_id}>
                                        <SelectTrigger className="w-[220px]">
                                          <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {categories.map((category) => (
                                            <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <div className="pt-2">
                                        {!isCompany && <Sa105Hint sa105Label={row.sa105_label} />}
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className={`px-3 py-2 text-right font-semibold ${row.type === 'income' ? 'text-green-700' : row.type === 'expense' ? 'text-red-700' : 'text-foreground'}`}>
                                  {formatCurrency(row.amount)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => startEdit(row)} title={row.is_journal ? 'Edit journal entry' : 'Edit transaction'}>
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setDeleteTarget(row);
                                        setDeleteApplyRecurring(false);
                                      }}
                                      title="Delete transaction"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                              {editTarget?.entry_id === row.entry_id && (
                                <tr className="border-b bg-royal-50/20">
                                  <td colSpan={6} className="px-3 py-3">
                                    <form className="space-y-3" onSubmit={handleEditSubmit}>
                                      <p className="text-sm font-medium text-royal-700">Editing transaction</p>
                                      <div className="grid gap-3 lg:grid-cols-5">
                                        <div className="space-y-1">
                                          <Label>Property</Label>
                                          <Select value={editForm.property_id} onValueChange={(value) => setEditForm((current) => ({ ...current, property_id: value }))}>
                                            <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                                            <SelectContent>
                                              {properties.map((property) => (
                                                <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label>Type</Label>
                                          <Select value={editForm.type} onValueChange={(value) => setEditForm((current) => ({ ...current, type: value, category: '' }))}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="expense">Expense</SelectItem>
                                              <SelectItem value="income">Income</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label>Category</Label>
                                          <Select value={editForm.category} onValueChange={(value) => setEditForm((current) => ({ ...current, category: value }))}>
                                            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                                            <SelectContent>
                                              {categoryOptions[editForm.type].map((category) => (
                                                <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label>Amount</Label>
                                          <Input type="number" min="0.01" step="0.01" value={editForm.amount} onChange={(event) => setEditForm((current) => ({ ...current, amount: event.target.value }))} />
                                        </div>
                                        <div className="space-y-1">
                                          <Label>Date</Label>
                                          <Input type="date" value={editForm.date} onChange={(event) => setEditForm((current) => ({ ...current, date: event.target.value }))} />
                                        </div>
                                      </div>
                                      <div className="space-y-1">
                                        <Label>Description</Label>
                                        <Input value={editForm.description} onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))} maxLength={500} />
                                        <p className="text-xs text-muted-foreground">{editDescriptionHelper}</p>
                                      </div>
                                      {row.recurring_group_id && (
                                        <label className="flex items-center gap-2 text-sm text-muted-foreground">
                                          <input type="checkbox" checked={editApplyRecurring} onChange={(event) => setEditApplyRecurring(event.target.checked)} />
                                          Apply changes to all recurring transactions in this series
                                        </label>
                                      )}
                                      <div className="flex justify-end gap-2">
                                        <Button type="button" variant="outline" onClick={resetEditForm} disabled={editSubmitting}>Reset</Button>
                                        <Button type="button" variant="outline" onClick={cancelEdit} disabled={editSubmitting}>Cancel</Button>
                                        <Button type="submit" disabled={editSubmitting}>
                                          {editSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
                                          Save changes
                                        </Button>
                                      </div>
                                    </form>
                                  </td>
                                </tr>
                              )}
                              {expanded && (
                                <tr className="border-b bg-muted/20">
                                  <td colSpan={6} className="px-3 py-3">
                                    <div className="overflow-x-auto rounded-md border bg-background">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="border-b text-left text-muted-foreground">
                                            <th className="px-3 py-2">Account</th>
                                            <th className="px-3 py-2 text-right">Debit</th>
                                            <th className="px-3 py-2 text-right">Credit</th>
                                            <th className="px-3 py-2">Description</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(row.lines || []).map((line, index) => (
                                            <tr key={`${row.entry_id}-${line.account}-${index}`} className="border-b last:border-0">
                                              <td className="px-3 py-2">{activeAccountOptions.find((account) => account.value === line.account)?.label || line.account}</td>
                                              <td className="px-3 py-2 text-right tabular-nums">{line.debit ? formatCurrency(line.debit) : '—'}</td>
                                              <td className="px-3 py-2 text-right tabular-nums">{line.credit ? formatCurrency(line.credit) : '—'}</td>
                                              <td className="px-3 py-2">{line.description || '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {summaryHasMore && (
                <div className="flex justify-center">
                  <Button variant="outline" onClick={() => fetchSummary({ append: true, offset: summaryOffset })} disabled={summaryLoadingMore}>
                    {summaryLoadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Load more
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {financeSubTab === 'analytics' && (
        <div className="space-y-8">
          {analyticsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-royal-600" /></div>
          ) : !analytics ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">No analytics data available for the selected period.</CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Total Income</CardTitle></CardHeader>
                  <CardContent className="py-5"><div className="text-xl font-bold text-green-700">{formatCurrency(analytics.totals?.income || 0)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Total Expenses</CardTitle></CardHeader>
                  <CardContent className="py-5"><div className="text-xl font-bold text-red-700">{formatCurrency(analytics.totals?.expenses || 0)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Net</CardTitle></CardHeader>
                  <CardContent className="py-5">
                    <div className={`text-xl font-bold ${(analytics.totals?.net || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(analytics.totals?.net || 0)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Expense Ratio</CardTitle></CardHeader>
                  <CardContent className="py-5"><div className="text-xl font-bold">{Number(analytics.totals?.expense_ratio || 0).toFixed(1)}%</div></CardContent>
                </Card>
              </div>

              <div className="grid gap-8 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-base">Expenses by {taxCategoryLabel} ({analyticsExpensePeriodLabel})</CardTitle>
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setAnalyticsExpenseGroupIndex((current) => Math.max(0, current - 1))}
                          disabled={!analyticsCanSwitchExpensePeriod || analyticsExpenseGroupIndex <= 0}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setAnalyticsExpenseGroupIndex((current) => Math.min(analyticsExpenseGroups.length - 1, current + 1))}
                          disabled={!analyticsCanSwitchExpensePeriod || analyticsExpenseGroupIndex >= analyticsExpenseGroups.length - 1}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-3 pb-6">
                    {analyticsExpenseCategories.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No expense data.</p>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={240}>
                          <PieChart>
                            <Pie
                              data={analyticsExpenseCategories}
                              dataKey="total"
                              nameKey="label"
                              cx={isMobileViewport ? '50%' : '68%'}
                              cy="50%"
                              outerRadius={isMobileViewport ? 86 : 80}
                              isAnimationActive={chartAnimationEnabled}
                              label={prefersReducedMotion ? false : ({ percent }) => `${(percent * 100).toFixed(0)}%`}
                              labelLine={!prefersReducedMotion}
                            >
                              {analyticsExpenseCategories.map((item, index) => (
                                <Cell key={`${item.category}-${index}`} fill={item.color} />
                              ))}
                            </Pie>
                            <ChartTooltip formatter={(value) => formatCurrency(value)} />
                            {!isMobileViewport && (
                              <Legend
                                layout="vertical"
                                verticalAlign="middle"
                                align="left"
                                content={() => (
                                  <ul className="space-y-1 text-xs">
                                    {analyticsExpenseCategories.map((item) => (
                                      <li key={`legend-${item.category}`} className="flex items-center gap-2">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                                        <span>{item.label}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                wrapperStyle={{ fontSize: 11, paddingRight: 12 }}
                              />
                            )}
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="mt-2 text-xs text-muted-foreground sm:hidden">Tip: tap chart sectors to view corresponding categories.</p>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <CardTitle className="text-base">{taxSummaryLabel} ({analyticsExpensePeriodLabel})</CardTitle>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!requireOnline(`${taxExportFormatLabel} PDF export`)) return;
                            window.open(taxSummaryPdfUrl, '_blank');
                          }}
                        >
                          <Download className="mr-2 h-4 w-4" />PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!requireOnline(`${taxExportFormatLabel} CSV export`)) return;
                            window.open(taxSummaryCsvUrl, '_blank');
                          }}
                        >
                          <FileSpreadsheet className="mr-2 h-4 w-4" />CSV
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-3 pb-6">
                    <div>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Income</div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{taxIncomeLabel}</span>
                        <span className="font-medium text-green-700">{formatCurrency(analyticsSelectedPeriodTotals.income)}</span>
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expenses</div>
                      {analyticsExpenseCategories.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No expense data.</p>
                      ) : (
                        <div className="space-y-2">
                          {analyticsExpenseCategories.map((item, index) => (
                            <div key={`${item.category}-${index}`} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                                <span>{item.label}</span>
                              </div>
                              <span className="font-medium">{formatCurrency(item.total)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{analyticsGroupingLabel} Cashflow (Chart)</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-3 pb-6">
                    <p className="mb-3 text-xs text-muted-foreground">Grouped by {analyticsGroupingLabel.toLowerCase()} periods.</p>
                    {(analytics.monthly_cashflow || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No monthly cashflow data.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={analytics.monthly_cashflow} margin={{ left: -20, right: 10 }}>
                          <XAxis dataKey="month_label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ChartTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="income" name="Income" fill="#10b981" isAnimationActive={chartAnimationEnabled} />
                          <Bar dataKey="expenses" name="Expenses" fill="#ef4444" isAnimationActive={chartAnimationEnabled} />
                          <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2} dot={false} isAnimationActive={chartAnimationEnabled} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">3-Month Forecast</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 pt-3 pb-6">
                    {(analytics.forecast || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No forecast data.</p>
                    ) : (
                      (analytics.forecast || []).map((month) => (
                        <div key={month.month_label} className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                          <span className="font-medium">{month.month_label}</span>
                          <span className="text-green-700 text-right">{formatCurrency(month.projected_income)}</span>
                          <span className="text-red-700 text-right">{formatCurrency(month.projected_expenses)}</span>
                          <span className={`text-right font-semibold ${month.projected_net >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(month.projected_net)}</span>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{analyticsGroupingLabel} Cashflow</CardTitle>
                </CardHeader>
                <CardContent className="px-0 py-4">
                  <div className="px-4 pb-2 text-xs text-muted-foreground">Grouped by {analyticsGroupingLabel.toLowerCase()} periods.</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="px-4 py-2">Period</th>
                          <th className="px-4 py-2 text-right">Income</th>
                          <th className="px-4 py-2 text-right">Expenses</th>
                          <th className="px-4 py-2 text-right">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {((analytics.monthly_cashflow || []).slice().reverse()).map((row) => (
                          <tr key={row.month_label} className="border-b last:border-0">
                            <td className="px-4 py-2">{row.month_label}</td>
                            <td className="px-4 py-2 text-right text-green-700">{formatCurrency(row.income)}</td>
                            <td className="px-4 py-2 text-right text-red-700">{formatCurrency(row.expenses)}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${row.net >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(row.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Per Property</CardTitle>
                </CardHeader>
                <CardContent className="px-0 py-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="px-4 py-2">Property</th>
                          <th className="px-4 py-2 text-right">Income</th>
                          <th className="px-4 py-2 text-right">Expenses</th>
                          <th className="px-4 py-2 text-right">Net</th>
                          <th className="px-4 py-2 text-right">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analytics.per_property || []).map((row) => (
                          <tr key={row.property_id} className="border-b last:border-0">
                            <td className="px-4 py-2">{row.property_name}</td>
                            <td className="px-4 py-2 text-right text-green-700">{formatCurrency(row.income)}</td>
                            <td className="px-4 py-2 text-right text-red-700">{formatCurrency(row.expenses)}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${row.net >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(row.net)}</td>
                            <td className="px-4 py-2 text-right">{Number(row.net_margin || 0).toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {financeSubTab === 'import' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" />CSV import</CardTitle>
              <CardDescription>Upload a bank-style CSV, map the columns, preview the entries, then import.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-3">
                <div className="space-y-1 lg:col-span-1">
                  <Label>Property</Label>
                  <Select value={importPropertyId} onValueChange={setImportPropertyId}>
                    <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 lg:col-span-2">
                  <Label>CSV file</Label>
                  <Input type="file" accept=".csv,text/csv" onChange={(event) => {
                    setImportFile(event.target.files?.[0] || null);
                    resetImportState();
                  }} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={detectImportColumns} disabled={importSubmitting || !importFile || !importPropertyId}>
                  {importSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Detect columns
                </Button>
                {(importStage === 'map' || importStage === 'preview') && (
                  <Button variant="outline" onClick={previewImport} disabled={importSubmitting}>
                    Preview import
                  </Button>
                )}
                {importStage === 'preview' && (
                  <Button onClick={commitImport} disabled={importSubmitting}>
                    {importSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Import entries
                  </Button>
                )}
              </div>

              {importHeaders.length > 0 && (
                <div className="space-y-3">
                  {importMappingErrors.length > 0 && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {importMappingErrors.map((message) => (
                        <p key={message}>{message}</p>
                      ))}
                    </div>
                  )}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {MAPPING_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label>{field.label}{field.required ? ' *' : ''}</Label>
                      <Select value={importMapping[field.key] || '__none__'} onValueChange={(value) => {
                        setImportMapping((current) => ({ ...current, [field.key]: value === '__none__' ? null : value }));
                        setImportMappingErrors([]);
                      }}>
                        <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not mapped</SelectItem>
                          {importHeaders.map((header) => (
                            <SelectItem key={`${field.key}-${header}`} value={header}>{header}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  </div>
                </div>
              )}

              {importPreviewRows.length > 0 && importStage === 'map' && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Detected rows</h4>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left">
                          {importHeaders.map((header) => <th key={header} className="px-3 py-2">{header}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreviewRows.map((row, index) => (
                          <tr key={index} className="border-b last:border-0">
                            {importHeaders.map((header) => <td key={`${index}-${header}`} className="px-3 py-2">{row[header] || '—'}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {importPreviewEntries.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Preview entries</h4>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2">Description</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreviewEntries.map((entry, index) => {
                          const opts = categoryOptions[entry.type] || categoryOptions.expense;
                          const effectiveCategory = importCategoryOverrides[String(index)] ?? entry.suggested_category ?? entry.category;
                          const isSuggested = !!entry.suggested_category && !importCategoryOverrides[String(index)];
                          return (
                            <tr key={`${entry.date}-${entry.description}-${index}`} className="border-b last:border-0">
                              <td className="px-3 py-2">{entry.date}</td>
                              <td className="px-3 py-2 capitalize">{entry.type}</td>
                              <td className="px-3 py-2 min-w-[160px]">
                                <select
                                  className="w-full rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                                  value={effectiveCategory}
                                  onChange={(e) => setImportCategoryOverrides((prev) => ({ ...prev, [String(index)]: e.target.value }))}
                                >
                                  {opts.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                                {isSuggested && (
                                  <span className="mt-0.5 block text-[10px] text-muted-foreground">suggested</span>
                                )}
                              </td>
                              <td className="px-3 py-2">{entry.description}</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(entry.amount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground">Categories are pre-filled based on past transactions. Adjust any row before importing.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4" />CSV Export</CardTitle>
              <CardDescription>Download cashflow transactions or {isCompany ? 'CT600' : 'SA105'} tax summary as CSV.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Cashflow Transactions</Label>
                <p className="text-xs text-muted-foreground">All transactions grouped by {cashflowGrouping === 'monthly' ? 'month' : cashflowGrouping === 'quarterly' ? 'quarter' : 'year'} with {isCompany ? 'CT600' : 'SA105'} categories.</p>
                <Button variant="outline" onClick={() => {
                  if (!requireOnline('Cashflow CSV export')) return;
                  window.open(exportUrl, '_blank');
                }}>
                  <Download className="mr-2 h-4 w-4" />Download Cashflow CSV
                </Button>
              </div>
              <div className="border-t pt-2" />
              <div className="space-y-2">
                <Label className="text-sm font-medium">{taxSummaryLabel}</Label>
                <p className="text-xs text-muted-foreground">Income and expenses by {isCompany ? 'CT600' : 'SA105'} categories for all {cashflowGrouping === 'monthly' ? 'months' : cashflowGrouping === 'quarterly' ? 'quarters' : 'year'}.</p>
                <Button variant="outline" onClick={() => {
                  if (!requireOnline(`${taxExportFormatLabel} CSV export`)) return;
                  window.open(taxSummaryCsvUrl, '_blank');
                }}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />Download {taxExportFormatLabel} CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {financeSubTab === 'reports' && (
        <div className="space-y-6">
          <Card className="border-border bg-muted/20">
            <CardContent className="py-3 text-sm text-muted-foreground">
              {isCompany
                ? 'MTD for Income Tax does not apply to limited companies. Corporation Tax filing deadlines apply — consult your accountant.'
                : 'Reports always cover the full selected tax year. The grouping selector applies to Transactions, Analytics, and Tax Summary views.'}
            </CardContent>
          </Card>
          {reportsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-royal-600" /></div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Profit & Loss</CardTitle></CardHeader>
                  <CardContent className="py-5">
                    <div className="text-xs text-muted-foreground">Net Profit / (Loss)</div>
                    <div className={`text-2xl font-bold ${(profitLoss?.net || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(profitLoss?.net || 0)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Trial Balance</CardTitle></CardHeader>
                  <CardContent className="py-5">
                    <div className="text-xs text-muted-foreground">Debits / credits</div>
                    <div className="text-lg font-semibold">{formatCurrency(trialBalance?.total_debit || 0)}</div>
                    <div className="text-xs text-muted-foreground">{trialBalance?.balanced ? 'Balanced' : 'Out of balance'}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-sm">Balance Sheet</CardTitle></CardHeader>
                  <CardContent className="py-5">
                    <div className="text-xs text-muted-foreground">Assets</div>
                    <div className="text-lg font-semibold">{formatCurrency(balanceSheet?.asset_total || 0)}</div>
                    <div className="text-xs text-muted-foreground">Liabilities + equity {formatCurrency(balanceSheet?.liabilities_and_equity || 0)}</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-base">Profit & Loss</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => {
                        if (!requireOnline('P&L PDF export')) return;
                        window.open(reportsPdfUrls.pnl, '_blank');
                      }}>
                        <Download className="mr-2 h-4 w-4" />PDF
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <div className="mb-3 text-sm font-semibold">Income</div>
                      <div className="space-y-2">
                        {(profitLoss?.income || []).map((row) => (
                          <div key={row.account} className="flex items-center justify-between text-sm">
                            <span className="inline-flex items-center gap-1.5">
                              <span>{cleanExpenseDisplayLabel(row.account_label, row.account_type)}</span>
                              {!isCompany && <Sa105Hint sa105Label={row.sa105_label} />}
                            </span>
                            <span>{formatCurrency(row.balance)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 border-t pt-3">
                        <div className="flex items-center justify-between text-sm font-semibold">
                          <span>Total Income</span>
                          <span>{formatCurrency(profitLoss?.income_total || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-3 text-sm font-semibold">Expenses</div>
                      <div className="space-y-2">
                        {(profitLoss?.expenses || []).map((row) => (
                          <div key={row.account} className="flex items-center justify-between text-sm">
                            <span className="inline-flex items-center gap-1.5">
                              <span>{cleanExpenseDisplayLabel(row.account_label, row.account_type)}</span>
                              {!isCompany && <Sa105Hint sa105Label={row.sa105_label} />}
                            </span>
                            <span>{formatCurrency(row.balance)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 border-t pt-3">
                        <div className="flex items-center justify-between text-sm font-semibold">
                          <span>Total Expenses</span>
                          <span>{formatCurrency(profitLoss?.expense_total || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md border bg-muted/30 px-4 py-4 text-sm font-semibold">
                      Net Profit / (Loss) {formatCurrency(profitLoss?.net || 0)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-base">Balance Sheet</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => {
                        if (!requireOnline('Balance Sheet PDF export')) return;
                        window.open(reportsPdfUrls.balanceSheet, '_blank');
                      }}>
                        <Download className="mr-2 h-4 w-4" />PDF
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div>
                      <div className="mb-2 font-semibold">Assets</div>
                      {(balanceSheet?.assets || []).map((row) => (
                        <div key={row.account} className="flex items-center justify-between py-1">
                          <span>{row.account_label}</span>
                          <span>{formatCurrency(row.balance)}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="mb-2 font-semibold">Liabilities</div>
                      {(balanceSheet?.liabilities || []).length === 0 ? (
                        <div className="py-1 text-muted-foreground">No liabilities</div>
                      ) : (
                        (balanceSheet?.liabilities || []).map((row) => (
                          <div key={row.account} className="flex items-center justify-between py-1">
                            <span>{row.account_label}</span>
                            <span>{formatCurrency(row.balance)}</span>
                          </div>
                        ))
                      )}
                    </div>
                    <div>
                      <div className="mb-2 font-semibold">Equity</div>
                      {(balanceSheet?.equity || []).map((row) => (
                        <div key={row.account} className="flex items-center justify-between py-1">
                          <span>{row.account === 'current_earnings' ? 'Net Profit / (Loss)' : row.account_label}</span>
                          <span>{formatCurrency(row.balance)}</span>
                        </div>
                      ))}
                    </div>
                    {balanceSheetCashBalance < 0 && (
                      <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                        Note: Cash is negative — you may not have recorded initial funding
                      </div>
                    )}
                    <div className="rounded-md border bg-muted/30 px-3 py-3 font-semibold">
                      Assets {formatCurrency(balanceSheet?.asset_total || 0)} = Liabilities + Equity {formatCurrency(balanceSheet?.liabilities_and_equity || 0)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base">Trial Balance</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => {
                      if (!requireOnline('Trial Balance PDF export')) return;
                      window.open(reportsPdfUrls.trialBalance, '_blank');
                    }}>
                      <Download className="mr-2 h-4 w-4" />PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-0 py-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="px-4 py-2">Account</th>
                          <th className="px-4 py-2">Type</th>
                          <th className="px-4 py-2 text-right">Debit</th>
                          <th className="px-4 py-2 text-right">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(trialBalance?.rows || []).map((row) => (
                          <tr key={row.account} className="border-b last:border-0">
                            <td className="px-4 py-2">
                              <span className="inline-flex items-center gap-1.5">
                                <span>{cleanExpenseDisplayLabel(row.account_label, row.account_type)}</span>
                                {!isCompany && <Sa105Hint sa105Label={row.sa105_label} />}
                              </span>
                            </td>
                            <td className="px-4 py-2 capitalize">{row.account_type}</td>
                            <td className="px-4 py-2 text-right">{formatCurrency(row.debit)}</td>
                            <td className="px-4 py-2 text-right">{formatCurrency(row.credit)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-semibold">
                          <td className="px-4 py-2">Totals</td>
                          <td className="px-4 py-2" />
                          <td className="px-4 py-2 text-right">{formatCurrency(trialBalance?.total_debit || 0)}</td>
                          <td className="px-4 py-2 text-right">{formatCurrency(trialBalance?.total_credit || 0)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteApplyRecurring(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.is_journal
                ? 'This deletes the full journal entry and all of its lines.'
                : deleteTarget?.recurring_group_id
                ? 'This will delete the selected transaction.'
                : 'This deletes the selected transaction.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget?.recurring_group_id && !deleteTarget?.is_journal && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={deleteApplyRecurring}
                onChange={(event) => setDeleteApplyRecurring(event.target.checked)}
              />
              Also delete all future recurring transactions in this series
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}
